import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import {
  invitationsApi,
  agenciesApi,
  workspacesApi,
  workspaceMembersApi,
  type Invitation,
  type AgencyMember,
  type Workspace,
} from '@/lib/api';
import { useAppStore } from '@/stores/appStore';
import { UserPlus, Trash2, Copy, Clock, CheckCircle2, Loader2, Users, ChevronDown, ChevronRight } from 'lucide-react';

const ROLE_OPTIONS = ['viewer', 'editor', 'admin'];
const ROLE_COLORS: Record<string, string> = {
  owner: 'bg-accent/20 text-accent',
  admin: 'bg-accent/20 text-accent',
  editor: 'bg-muted text-muted-foreground',
  viewer: 'bg-muted text-muted-foreground',
};

export function SettingsMembers() {
  const { getToken } = useAuth();
  const { currentAgency } = useAppStore();
  const qc = useQueryClient();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState('editor');
  const [inviteMsg, setInviteMsg] = useState<string | null>(null);

  const { data: invitationData, isLoading: invLoading } = useQuery({
    queryKey: ['invitations', currentAgency?.id],
    queryFn: async () => {
      const token = await getToken();
      return invitationsApi.list(token!, currentAgency!.id);
    },
    enabled: !!currentAgency,
  });

  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['agency-members', currentAgency?.id],
    queryFn: async () => {
      const token = await getToken();
      return agenciesApi.listMembers(token!, currentAgency!.id);
    },
    enabled: !!currentAgency,
  });

  const { data: workspacesData } = useQuery({
    queryKey: ['workspaces', currentAgency?.id],
    queryFn: async () => {
      const token = await getToken();
      return workspacesApi.list(token!, currentAgency!.id);
    },
    enabled: !!currentAgency,
  });

  const createInvite = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return invitationsApi.create(token!, {
        agencyId: currentAgency!.id,
        email: email.trim(),
        role,
      });
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['invitations', currentAgency?.id] });
      const invitedEmail = res.invitation?.email ?? email.trim();
      setEmail('');
      // When Cloudflare Email delivered it, just confirm. Otherwise fall back to
      // copying the link so the inviter can still share it manually.
      if (res.emailed) {
        setInviteMsg(`Invitation emailed to ${invitedEmail}.`);
        setTimeout(() => setInviteMsg(null), 4000);
      } else if (res.invitation?.inviteUrl) {
        navigator.clipboard.writeText(res.invitation.inviteUrl).catch(() => {});
        setInviteMsg("Email couldn't be sent — invite link copied to clipboard instead.");
        setTimeout(() => setInviteMsg(null), 5000);
      }
    },
  });

  const revokeInvite = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return invitationsApi.revoke(token!, id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invitations', currentAgency?.id] }),
  });

  const removeMember = useMutation({
    mutationFn: async (memberId: string) => {
      const token = await getToken();
      return agenciesApi.removeMember(token!, currentAgency!.id, memberId);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agency-members', currentAgency?.id] }),
  });

  const changeRole = useMutation({
    mutationFn: async ({ memberId, role }: { memberId: string; role: string }) => {
      const token = await getToken();
      return agenciesApi.updateMemberRole(token!, currentAgency!.id, memberId, role);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agency-members', currentAgency?.id] }),
  });

  const invitations = invitationData?.invitations ?? [];
  const pending = invitations.filter((i) => !i.accepted_at);
  const members = membersData?.members ?? [];
  const workspaces = workspacesData?.workspaces ?? [];

  if (!currentAgency) {
    return <p className="text-sm text-muted-foreground">Select an agency to manage members.</p>;
  }

  return (
    <div className="space-y-8 max-w-lg">
      {/* Active members */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">Team Members</h2>
          <span className="text-xs text-muted-foreground">({members.length})</span>
        </div>
        {membersLoading ? (
          <div className="space-y-2">
            {[...Array(2)].map((_, i) => <div key={i} className="h-14 bg-muted rounded-lg animate-pulse" />)}
          </div>
        ) : members.length === 0 ? (
          <p className="text-sm text-muted-foreground">No members yet.</p>
        ) : (
          <div className="space-y-2">
            {members.map((m) => (
              <MemberRow
                key={m.id}
                member={m}
                workspaces={workspaces}
                onRemove={() => {
                  if (window.confirm(`Remove ${m.name ?? m.email} from this agency?`))
                    removeMember.mutate(m.id);
                }}
                onChangeRole={(role) => changeRole.mutate({ memberId: m.id, role })}
                busy={removeMember.isPending || changeRole.isPending}
              />
            ))}
          </div>
        )}
      </div>

      {/* Workspace assignment info */}
      {workspaces.length > 0 && (
        <div className="bg-muted/20 border border-border rounded-lg p-4">
          <p className="text-xs font-medium text-foreground mb-1">Workspace Access</p>
          <p className="text-xs text-muted-foreground">
            Editors and viewers see every brand by default. Use "Brand access" on a member to limit
            them to specific workspaces — with a per-brand role (e.g. editor on one brand, viewer on
            another). Owners and admins always see everything.
          </p>
        </div>
      )}

      {/* Invite form */}
      <div>
        <h2 className="text-sm font-semibold text-foreground mb-1">Invite Team Member</h2>
        <p className="text-xs text-muted-foreground mb-4">
          Send an invitation link to add a new member to this agency.
        </p>

        <div className="bg-card border border-border rounded-lg p-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-foreground block mb-1">
              Email address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@company.com"
              className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-foreground block mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-accent"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              Admin: manages members and settings. Editor: creates and publishes posts.
              Viewer: read-only access.
            </p>
          </div>
          <button
            onClick={() => createInvite.mutate()}
            disabled={!email.trim() || createInvite.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-md text-sm font-medium hover:bg-accent/90 disabled:opacity-50 transition-colors"
          >
            {createInvite.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <UserPlus className="h-4 w-4" />
            )}
            Send Invitation
          </button>

          {inviteMsg && (
            <p className="text-xs text-success">{inviteMsg}</p>
          )}
          {createInvite.isError && (
            <p className="text-xs text-destructive">Failed to create invitation. Check your permissions.</p>
          )}
        </div>
      </div>

      {/* Pending invitations */}
      {invLoading ? (
        <div className="space-y-2">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="h-14 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      ) : pending.length > 0 ? (
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Pending Invitations ({pending.length})
          </h3>
          <div className="space-y-2">
            {pending.map((inv: Invitation) => (
              <InviteRow
                key={inv.id}
                inv={inv}
                onRevoke={() => revokeInvite.mutate(inv.id)}
                revoking={revokeInvite.isPending}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MemberRow({
  member,
  workspaces,
  onRemove,
  onChangeRole,
  busy,
}: {
  member: AgencyMember;
  workspaces: Workspace[];
  onRemove: () => void;
  onChangeRole: (role: string) => void;
  busy: boolean;
}) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const scopable = member.role === 'editor' || member.role === 'viewer';

  // Per-workspace assignments across all this agency's workspaces for this user.
  const { data: assignmentData } = useQuery({
    queryKey: ['workspace-assignments', member.user_id, workspaces.map((w) => w.id).join(',')],
    enabled: expanded && scopable && workspaces.length > 0,
    queryFn: async () => {
      const token = await getToken();
      const results = await Promise.all(
        workspaces.map(async (w) => {
          const { members } = await workspaceMembersApi.list(token!, w.id);
          const mine = members.find((x) => x.user_id === member.user_id);
          return [w.id, mine?.role ?? null] as const;
        })
      );
      return Object.fromEntries(results) as Record<string, 'editor' | 'viewer' | null>;
    },
  });

  const setAssignment = useMutation({
    mutationFn: async ({
      workspaceId,
      role,
    }: {
      workspaceId: string;
      role: 'editor' | 'viewer' | null;
    }) => {
      const token = await getToken();
      if (role === null) return workspaceMembersApi.unassign(token!, workspaceId, member.user_id);
      return workspaceMembersApi.assign(token!, workspaceId, member.user_id, role);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workspace-assignments', member.user_id] });
      // workspace_scoped on the member row changes with assignments.
      qc.invalidateQueries({ queryKey: ['agency-members', member.agency_id] });
    },
  });

  const assignments = assignmentData ?? {};
  const assignedCount = Object.values(assignments).filter(Boolean).length;

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0">
          <span className="text-xs font-semibold text-foreground">
            {(member.name ?? member.email)?.[0]?.toUpperCase() ?? '?'}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-foreground font-medium truncate">{member.name ?? member.email}</p>
          <p className="text-xs text-muted-foreground truncate">{member.email}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {member.role === 'owner' ? (
            <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS.owner}`}>
              owner
            </span>
          ) : (
            <select
              value={member.role}
              disabled={busy}
              onChange={(e) => onChangeRole(e.target.value)}
              className="bg-input border border-border rounded-md px-2 py-1 text-xs text-foreground outline-none focus:ring-1 focus:ring-accent"
              title="Change role"
            >
              {['viewer', 'editor', 'admin'].map((r) => (
                <option key={r} value={r}>
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </option>
              ))}
            </select>
          )}
          {scopable && workspaces.length > 0 && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground hover:text-foreground rounded-md hover:bg-muted/40 transition-colors"
              title="Limit this member to specific brands"
            >
              {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              Brand access
            </button>
          )}
          {member.role !== 'owner' && (
            <button
              onClick={onRemove}
              disabled={busy}
              className="p-1.5 text-muted-foreground hover:text-destructive rounded-md hover:bg-destructive/10 transition-colors"
              title="Remove member"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {expanded && scopable && (
        <div className="border-t border-border px-4 py-3 space-y-2">
          <p className="text-[11px] text-muted-foreground">
            {assignedCount === 0 && member.workspace_scoped
              ? 'No brand access — the brands this member was limited to were deleted. Assign a brand to restore access.'
              : assignedCount === 0
              ? 'No brand restrictions — this member can access every workspace.'
              : `Restricted to ${assignedCount} brand${assignedCount === 1 ? '' : 's'}. Unassigned brands are hidden from them.`}
          </p>
          {workspaces.map((w) => (
            <div key={w.id} className="flex items-center gap-3">
              <p className="text-xs text-foreground flex-1 min-w-0 truncate">{w.name}</p>
              <select
                value={assignments[w.id] ?? 'none'}
                disabled={setAssignment.isPending}
                onChange={(e) =>
                  setAssignment.mutate({
                    workspaceId: w.id,
                    role: e.target.value === 'none' ? null : (e.target.value as 'editor' | 'viewer'),
                  })
                }
                className="bg-input border border-border rounded-md px-2 py-1 text-xs text-foreground outline-none focus:ring-1 focus:ring-accent"
              >
                <option value="none">Not assigned</option>
                <option value="editor">Editor</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InviteRow({
  inv,
  onRevoke,
  revoking,
  accepted,
}: {
  inv: Invitation;
  onRevoke?: () => void;
  revoking?: boolean;
  accepted?: boolean;
}) {
  const inviteUrl = `${window.location.origin}/invite/${inv.token ?? ''}`;

  const copyLink = () => {
    if (inv.token) navigator.clipboard.writeText(inviteUrl).catch(() => {});
  };

  return (
    <div className="flex items-center gap-3 bg-card border border-border rounded-lg px-4 py-3">
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground font-medium truncate">{inv.email}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-muted-foreground capitalize">{inv.role}</span>
          {accepted ? (
            <span className="flex items-center gap-0.5 text-xs text-success">
              <CheckCircle2 className="h-3 w-3" />
              Accepted
            </span>
          ) : (
            <span className="flex items-center gap-0.5 text-xs text-warning">
              <Clock className="h-3 w-3" />
              Pending · expires {new Date(inv.expires_at).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>
      {!accepted && (
        <div className="flex items-center gap-1 shrink-0">
          {inv.token && (
            <button
              onClick={copyLink}
              className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted/40 transition-colors"
              title="Copy invite link"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            onClick={onRevoke}
            disabled={revoking}
            className="p-1.5 text-muted-foreground hover:text-destructive rounded-md hover:bg-destructive/10 transition-colors"
            title="Revoke invitation"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
